import type { ScreenerQuestion } from '../types/screener';

const normalizeQuestionText = (text: string | undefined): string =>
  (text ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * True for the PHQ-9 functional-difficulty question ("If you checked off any
 * problems, how difficult have these problems made it..."). It is not part of
 * the 0-27 total. Athena has no "unscored" flag and its position and ids differ
 * between template versions, so we match on the wording.
 */
export function isUnscoredQuestion(question: ScreenerQuestion): boolean {
  const text = normalizeQuestionText(question.question);
  return (
    text.includes('if you checked off any problems') &&
    text.includes('how difficult')
  );
}

/**
 * Calculates the score for a single answered screener question.
 * Unscored questions (see `isUnscoredQuestion`) always return 0.
 *
 * Priority: if the question carries `options` with explicit `optionvalue`,
 * use that (handles non-sequential scoring like 0,1,3,5). Otherwise fall
 * back to the index of the answer within `possibleanswers` (sequential 0-N).
 */
export function calculateQuestionScore(
  question: ScreenerQuestion,
  answer: string,
): number {
  if (isUnscoredQuestion(question)) return 0;

  const hasOptions = question.options && question.options.length > 0;

  if (hasOptions) {
    const matched = question.options!.find((opt) => opt.optiontext === answer);
    return matched ? Number(matched.optionvalue) || 0 : 0;
  }

  if (question.possibleanswers && question.possibleanswers.length > 0) {
    const idx = question.possibleanswers.indexOf(answer);
    return idx >= 0 ? idx : 0;
  }

  return 0;
}
